import { useEffect, useState } from 'react';
import { Report, ReportMetadata, ServiceItem, ServiceTransaction, IntervalConfig, IntervalType, UserProfile } from '../types';
import { supabase } from '../services/supabaseClient';
import { calculateNextDueDate, heuteBerlin, parseDateLocal, planInspectionScheduleUpdate } from '../services/dateService';
import { saveReportSnapshot, saveProjectToCloud, loadProjectById } from '../services/cloudService';
import { PerformerChoice } from '../services/performerChoice';
import { buildActorName, realActorName } from '../services/actorName';
import { followUpBillingServiceId, redirectMainBookingsToFlat, reportRefOf } from '../services/reportBooking';
import { FOREIGN_ERST_BEGRUENDEN, readOnlyMessage } from '../services/readOnlyNotice';
import { useForeignAccess } from '../contexts/ForeignAccessContext';
import { clearPresence } from '../services/participantSelection';
import { markParticipantPrompt, participantPromptKey } from '../services/participantPrompt';
import { planFlatMonthBooking } from '../services/flatRateBooking';
import { bookingIsFlatProof, isInspectionProofEntry, planFlatProofEntry } from '../services/flatRateLogic';
import { getServiceLog, JournalEntryStatement, JournalMirrorRequest, mirrorEntriesToJournal, queueJournalMirror } from '../services/serviceJournal';
import { hatBestandteile } from '../services/bestandteile/bestandteilLogic';

/** Bucht eine Nutzungsmenge auf eine Leistung und setzt den Status bei Erreichen der Vertragsmenge auf 'done'. */
export const bookServiceUsage = (service: ServiceItem, amount: number): ServiceItem => {
    const newUsed = (service.quantityUsed || 0) + amount;
    const isDone = service.quantityContracted > 0 && newUsed >= service.quantityContracted;
    return { ...service, quantityUsed: newUsed, status: isDone ? 'done' : service.status };
};

/** Was die Schnellbuchung ueber die neue Protokollzeile weiss. */
export interface TrackServiceEntryInput {
    id: string;
    date: string;
    /** Buchungsvorgang (ISO) — nur die Nachweiszeile fuehrt ihn. */
    bookedAt: string;
    performerId: string;
    performerName: string;
    description: string;
    nepReason?: string;
}

export interface TrackServicePlan {
    services: ServiceItem[];
    logEntry: ServiceTransaction;
    /** Nachweiszeile auf einer Pauschale: Menge 0, quantityUsed/status unveraendert. */
    flatProof: boolean;
    /** Belegt die Zeile eine Begehung (Begehungsrhythmus fortschreiben)? */
    isInspection: boolean;
    /** Aussage fuers Journal — hier nie gesetzt: die Abbildung (inferInspectionCoverage) entscheidet. */
    statements?: Record<string, JournalEntryStatement>;
    /**
     * Gewaehlt war eine von der Begehungspauschale gedeckte Begehungsposition
     * (z. B. 2.7.10) — gebucht wurde der Nachweis auf der Pauschale. Die Id der
     * urspruenglich gewaehlten Position, fuer den Hinweis in der Oberflaeche.
     */
    redirectedFrom?: string;
}

/**
 * Die Weiche der Schnellbuchung (ServiceTrackingModal → handleTrackService),
 * rein und ohne Hook pruefbar.
 *
 * Ziel ist eine Pauschale: NACHWEISZEILE (planFlatProofEntry, Menge 0,
 * NW-…), quantityUsed/status der Pauschale bleiben stehen. Vorher lief auch
 * diese Buchung ueber bookServiceUsage und hob quantityUsed an — ein Monat
 * Geld ohne FLAT-Schluessel, den weder der Monatsriegel noch der Vorschlag
 * offener Monate je gesehen haben.
 *
 * Ziel ist eine von der Begehungspauschale GEDECKTE Begehungsposition (etwa
 * 2.7.10 im Pauschalprojekt): ebenfalls der Nachweis auf der Pauschale —
 * dieselbe Regel wie beim Versand (redirectMainBookingsToFlat), sonst zaehlte
 * die Schnellbuchung weiter Menge auf eine Position, die nach dem
 * Betreiber-Entscheid nichts mehr bekommt.
 *
 * Jedes andere Ziel: unveraendert Menge auf die Position.
 *
 * PAKETPOSITION (Position mit Bestandteilen): nie. Pakete bucht nur der
 * Bestandteile-Weg (Kernregel 7, docs/positionsbestandteile-plan.md) — die
 * Position wird gebucht, wenn der letzte Bestandteil erledigt ist, mit
 * `BST-`-Schluessel und ausloesender Person. Eine Mengenbuchung hier liefe an
 * Einmal-Riegel und Nachweis vorbei. Die Oberflaeche leitet vorher in den
 * Bestandteile-Dialog um; das hier ist die zweite Reihe.
 *
 * null = die Position gibt es in dieser Liste nicht, oder sie ist ein Paket.
 */
export const planTrackServiceBooking = (args: {
    services: ServiceItem[];
    serviceId: string;
    amount: number;
    entry: TrackServiceEntryInput;
}): TrackServicePlan | null => {
    const gewaehlt = args.services.find(s => s.id === args.serviceId);
    if (!gewaehlt) return null;
    // Pakete bucht nur der Bestandteile-Weg (Kernregel 7).
    if (hatBestandteile(gewaehlt)) return null;
    const umlenkung = redirectMainBookingsToFlat([{ serviceId: args.serviceId, quantity: args.amount || 1 }], args.services)
        .redirectedToFlat;
    const zielId = umlenkung ? umlenkung.flatServiceId : args.serviceId;
    const index = args.services.findIndex(s => s.id === zielId);
    if (index === -1) return null;
    const target = args.services[index];
    // Auch nach einer Umlenkung nie auf ein Paket (Kernregel 7).
    if (hatBestandteile(target)) return null;
    const services = [...args.services];
    const { entry } = args;

    if (bookingIsFlatProof(target)) {
        const logEntry: ServiceTransaction = {
            ...planFlatProofEntry({
                id: entry.id,
                flatServiceId: target.id,
                date: entry.date,
                bookedAt: entry.bookedAt,
                performerId: entry.performerId,
                performerName: entry.performerName,
                description: entry.description,
                // Umgelenkt von einer Begehungsposition: das IST eine Begehung,
                // auch unter einer 'all'-/'explicit'-Pauschale (Opus-Review 30.09.).
                begehung: !!umlenkung || target.flatRate?.scope === 'inspection',
            }),
            ...(entry.nepReason ? { nepReason: entry.nepReason } : {}),
        };
        // Ohne Bericht zaehlt die Zeile nur auf einer reinen
        // Begehungspauschale als Begehung — auf 'all' kann eine Handbuchung
        // genauso eine Baubesprechung sein (isInspectionProofEntry). Das
        // steuert Rhythmus und Zaehler.
        const isInspection = isInspectionProofEntry(logEntry, target);
        // KEINE Journal-Aussage: die Beschreibung ist freier Text aus dem
        // Dialog. „Belegt die Begehung eines Berichts" erschliesst die
        // Abbildung (inferInspectionCoverage kennt die Nachweiszeile), und die
        // ist mit der SQL-Nachspiegelung (Nr. 134) abgeglichen — eine eigene
        // Aussage hier wich bei Texten wie „Bericht #7 abgeschlossen
        // (nachgetragen)" von ihr ab (Codex R2, P2).
        return {
            services,
            logEntry,
            flatProof: true,
            isInspection,
            ...(umlenkung ? { redirectedFrom: args.serviceId } : {}),
        };
    }

    services[index] = bookServiceUsage(target, args.amount);
    const logEntry: ServiceTransaction = { id: entry.id, date: entry.date, serviceId: target.id, quantity: args.amount, performerId: entry.performerId, performerName: entry.performerName, isSubstitution: false, description: entry.description, ...(entry.nepReason ? { nepReason: entry.nepReason } : {}) };
    return { services, logEntry, flatProof: false, isInspection: target.assignment === 'inspection' };
};

/**
 * Gibt es ueberhaupt einen Bericht, den das Anlegen der naechsten Begehung
 * archivieren muesste?
 *
 * Vor der ERSTEN Begehung steht der Zaehler auf 0 und der Bericht ist eine
 * leere Huelle ohne Begehungsdatum. Archiviert wurde sie trotzdem — die RPC
 * save_report_snapshot bekam `p_date = ""` und antwortete mit 22007
 * ("invalid input syntax for type date"). Zusammen mit dem Abbruch bei
 * fehlgeschlagener Archivierung hiess das: auf einem frischen Projekt liess
 * sich die erste Begehung gar nicht anlegen.
 *
 * Das Begehungsdatum ist dabei das richtige Unterscheidungsmerkmal, nicht der
 * Zaehler: eine durchgefuehrte Begehung hat immer einen Tag — er ist das
 * fachliche Schluesselfeld des Archivs. Ist er unbrauchbar, hat die Begehung
 * nicht stattgefunden und es gibt nichts zu sichern.
 */
export const isArchivableReport = (meta: { date?: string } | null | undefined): boolean =>
    parseDateLocal(meta?.date) !== null;

/**
 * Wessen Name kommt ins Archiv (`report_history.conductor_name`)?
 *
 * Der VERSENDER des Berichts — der Mensch, der ihn dem Kunden geschickt und
 * damit gebucht hat. Genau er steht im Leistungsnachweis neben der Begehung.
 *
 * Bisher stand hier `currentUser`, also wer gerade „naechste Begehung" klickt.
 * Das ist im Regelfall der Innendienst, und der Name friert ein: die RPC
 * `save_report_snapshot` setzt `conductor_name` NUR beim Anlegen, ein spaeterer
 * Versand korrigiert ihn nicht mehr (cloudService). In Projekt 6172 liefen
 * Archiv und Buchung dadurch auseinander — Berichte 88/89/90 tragen im Archiv
 * einen anderen Namen als in ihrer Versandbuchung.
 *
 * Der Versender steht in der Buchung des Berichts, die der Versandpfad
 * (EmailModal) unter dem `reportRef` dieses Berichts ablegt. Findet sich keine
 * (nie versendet), bleibt es beim handelnden Nutzer — mehr weiss niemand.
 * `realActorName` haelt dabei Stubs wie 'System (Linked)' heraus.
 */
export const archiveActorForReport = (
    meta: { inspectionNumber?: number; sectionId?: string | null } | null | undefined,
    serviceLog: Array<{ reportRef?: string | null; performerName?: string | null }> | null | undefined,
    currentUser: UserProfile | null,
): string | UserProfile | null => {
    const ref = reportRefOf(meta?.inspectionNumber || 0, meta?.sectionId);
    const versand = (serviceLog || []).find(e => e?.reportRef === ref);
    return realActorName(versand?.performerName) || currentUser;
};

/**
 * Hat das Projekt einen Begehungsrhythmus, aus dem sich ein Folgetermin
 * (next_scheduled_date) ableiten laesst?
 *
 * SiFa: NIE. Eine Betreuung hat keinen Turnus — und ein `intervalConfig`,
 * das im JSON eines SiFa-Projekts liegt, ist Altbestand aus dem D1-Bug
 * (ProjectStats schrieb bei jedem Statuswechsel die Weekly-Vorgabe des Hubs,
 * docs/sifa-betreuung-plan.md). Die Weiche darf deshalb nicht mehr an
 * `!meta.intervalConfig` haengen, sondern nur am Projekttyp.
 */
export const hatBegehungsrhythmus = (meta: Pick<ReportMetadata, 'projectType'> | null | undefined): boolean =>
    meta?.projectType !== 'sifa';

/**
 * intervalConfig des Folgeberichts nach dem Anlegen der naechsten Begehung.
 *
 * SiGeKo/HSE: Turnus (Vorgabe weekly) mit neu berechnetem Folgetermin.
 * SiFa: unveraendert zurueck — weder ein Weekly-Fallback noch ein aus einem
 * Turnus berechneter Termin, auch wenn ein altes intervalConfig vorliegt.
 */
export const planIntervalConfigNachBegehung = (
    meta: Pick<ReportMetadata, 'projectType' | 'intervalConfig'>,
    neuesBegehungsdatum: string,
): IntervalConfig | undefined => {
    if (!hatBegehungsrhythmus(meta)) return meta.intervalConfig;
    const current: IntervalConfig = meta.intervalConfig || { type: 'weekly' as IntervalType };
    return { ...current, nextScheduledDate: calculateNextDueDate(neuesBegehungsdatum, current) };
};

/**
 * Handy-Knopf „Begehung #N fortsetzen": direkt ins Modul nur mit Schreibrecht.
 * Lesemodus und fremdes Projekt ohne Begruendung gehen ueber den
 * Begehungsdialog (Ansehen bzw. erst die Begruendung).
 */
export const fortsetzenWeg = (isReadOnly: boolean, begruendungAusstehend: boolean): 'modul' | 'dialog' =>
    isReadOnly || begruendungAusstehend ? 'dialog' : 'modul';

interface UseReportActionsProps {
    report: Report;
    onUpdate: (report: Report) => void;
    onCreateVersion?: (report: Report) => void;
    onNavigate: (module: 'editor' | 'vorank' | 'unterlage' | 'module-sigeko-inspection' | 'project-map' | 'module-archive' | 'module-sent-reports' | 'sigeplan') => void;
    activeSectionId: string | null;
    dbCurrentNr?: number;
    currentUser?: UserProfile | null;
    linkedParent: Report | null;
    setLinkedParent: (report: Report | null) => void;
    setDbCurrentNr: (nr: number | undefined) => void;
    setIsLegacyCorrected: (val: boolean | null) => void;
    /**
     * Schreibgeschuetzt geoeffnetes Projekt (Admin → Offene Positionen).
     * Alle drei schreibenden Handler hier gehen an App.handleUpdateReport
     * vorbei — sie sprechen Supabase bzw. cloudService direkt an. Der Riegel
     * muss deshalb im Hook stehen und nicht nur an den UI-Einstiegen.
     */
    isReadOnly?: boolean;
}

export const useReportActions = ({
    report, onUpdate, onCreateVersion, onNavigate, activeSectionId, dbCurrentNr,
    currentUser, setLinkedParent, setDbCurrentNr, setIsLegacyCorrected, isReadOnly = false,
}: UseReportActionsProps) => {
    const meta = report.metadata;
    const globalData = meta.globalData || { projectId: 'temp-id', status: 'planning', budget: 0, budgetUsed: 0, notes: [], services: [], serviceLog: [], scope: {}, sections: [], enableSections: false };

    const [nextDate, setNextDate] = useState(() => heuteBerlin());
    const [nextTime, setNextTime] = useState('08:00');
    const [showLoadDialog, setShowLoadDialog] = useState(false);
    const [trackingModalState, setTrackingModalState] = useState<{ isOpen: boolean, service: ServiceItem | null }>({ isOpen: false, service: null });

    /**
     * Fremdes Projekt ohne Begruendung von heute (SQL Nr. 128, App.tsx).
     *
     * Die Schreibwege hier sagen dann nicht „Nur-Ansicht, ueber die
     * Projektliste oeffnen" — das stimmt fuer diesen Fall nicht und fuehrt
     * nirgendwohin —, sondern oeffnen den Begruendungsdialog. Ohne Provider
     * (Tests, Einsatz ausserhalb der App) ist `pending` false und alles bleibt
     * wie bisher.
     */
    const foreignAccess = useForeignAccess();

    /**
     * Begehungsdialog oeffnen, sobald der Bericht nach einer Begruendung
     * wieder stimmt.
     *
     * Nicht direkt nach dem `await`: die Begruendung laedt das Projekt neu, und
     * `meta` in dieser Closure ist noch der Stand vom Ansehen. Der Merker
     * laeuft durch einen Render; der Effekt liest Datum und Uhrzeit dann aus
     * dem frischen Bericht.
     */
    const [openLoadDialogPending, setOpenLoadDialogPending] = useState(false);
    useEffect(() => {
        if (!openLoadDialogPending) return;
        setOpenLoadDialogPending(false);
        setNextDate(meta.date || heuteBerlin());
        setNextTime(meta.time || '08:00');
        setShowLoadDialog(true);
    }, [openLoadDialogPending, meta.date, meta.time]);

    /**
     * Fremdes Projekt: erst die Begruendung abfragen, dann den Dialog wie
     * bisher oeffnen. Wer „Nur ansehen" waehlt, bekommt ihn im Lesemodus —
     * genau wie ohne Begruendungsweg.
     */
    const handleOpenEditorFromGrid = async () => {
        if (foreignAccess.pending) {
            await foreignAccess.requestJustification();
            setOpenLoadDialogPending(true);
            return;
        }
        setNextDate(meta.date || heuteBerlin());
        setNextTime(meta.time || '08:00');
        setShowLoadDialog(true);
    };

    // ═══════════════════════════════════════════════════════════════
    // FIX: handleReportAction — recalculates nextScheduledDate
    // ═══════════════════════════════════════════════════════════════
    const handleReportAction = async (mode: 'edit' | 'next', billingData?: { serviceId: string, quantity: number, sectionId?: string }, dateOverride?: string, timeOverride?: string) => {
        if (mode === 'edit') {
            const finalSectionId = activeSectionId !== 'all' ? activeSectionId : undefined;
            const updatedMeta = { ...meta, sectionId: finalSectionId as string };
            if (dateOverride) updatedMeta.date = dateOverride;
            if (timeOverride) updatedMeta.time = timeOverride;
            onUpdate({ ...report, metadata: updatedMeta });
            onNavigate('module-sigeko-inspection');
        } else {
            // 'next' archiviert den laufenden Bericht (saveReportSnapshot) und
            // zaehlt den Begehungszaehler in der DB hoch — beides an
            // App.handleUpdateReport vorbei. Der Lesemodus muss hier abbrechen,
            // sonst legt ein reines Ansehen echte Archivzeilen an.
            // Ansehen und Weiterspringen ('edit') bleiben erlaubt.
            if (isReadOnly) {
                // Fremdes Projekt: statt der Absage die Begruendung. Angelegt
                // wird hier trotzdem nichts — diese Closure kennt nur den Stand
                // vom Ansehen. Nach der Begruendung geht der Dialog mit dem
                // frischen Bericht wieder auf, der zweite Klick legt an.
                if (foreignAccess.pending) {
                    setShowLoadDialog(false);
                    void foreignAccess.requestJustification().then((ok) => {
                        if (ok) setOpenLoadDialogPending(true);
                    });
                    return;
                }
                alert(readOnlyMessage('Es wurde keine neue Begehung angelegt und nichts archiviert.'));
                setShowLoadDialog(false);
                return;
            }
            const currentNr = Math.max(dbCurrentNr || 0, report.metadata.inspectionNumber || 0);
            const nextNr = currentNr + 1;
            const sectionIdToUse = billingData?.sectionId || (activeSectionId !== 'all' ? activeSectionId : undefined);

            // Vor der ersten Begehung gibt es nichts zu archivieren — siehe
            // isArchivableReport. Ohne diese Bedingung scheiterte die RPC an
            // einem leeren Begehungsdatum, und der Abbruch unten sperrte das
            // Anlegen der ersten Begehung komplett.
            if (globalData.projectId && globalData.projectId !== 'temp-id' && isArchivableReport(report.metadata)) {
                // Abbruch statt stillem Weiterlaufen: schlug die Archivierung
                // fehl, wurde die Begehungsnummer trotzdem hochgezählt und der
                // gerade abgeschlossene Bericht landete NIE im Archiv — die
                // Lücke fällt erst Wochen später bei Rechnung/Leistungsnachweis
                // auf und ist dann nicht mehr rekonstruierbar. Der Versandpfad
                // macht es bereits so (cloudService wirft, EmailModal meldet).
                //
                // Der Archivname kommt aus der Versandbuchung dieses Berichts
                // (archiveActorForReport); erst wenn es keine gibt, zaehlt der
                // handelnde Nutzer — dann ohne zusaetzlichen Profil-Roundtrip.
                try {
                    await saveReportSnapshot(
                        report,
                        undefined,
                        archiveActorForReport(report.metadata, globalData.serviceLog, currentUser),
                    );
                } catch (e) {
                    console.error('Archivierung des Berichts fehlgeschlagen', e);
                    alert('Fehler: Der aktuelle Bericht konnte nicht archiviert werden. Die nächste Begehung wurde NICHT angelegt — bitte Verbindung prüfen und erneut versuchen.');
                    return; // Dialog bleibt offen, damit der Nutzer direkt erneut auslösen kann.
                }
            }

            let updatedServices = [...(globalData.services || [])];
            let updatedServiceLog = [...(globalData.serviceLog || [])];
            let updatedSections = globalData.sections ? [...globalData.sections] : [];

            const finalNextDate = dateOverride || nextDate;
            const finalNextTime = timeOverride || nextTime;

            if (sectionIdToUse) {
                updatedSections = updatedSections.map(s => s.id === sectionIdToUse ? { ...s, currentInspectionNumber: (s.currentInspectionNumber || 0) + 1, lastDate: finalNextDate } : s);
            }

            // ── FIX: Recalculate nextScheduledDate from the NEW inspection date ──
            // SiFa-Dauerprojekt: KEIN weekly-Vorschlag und KEIN Folgetermin aus
            // einem Turnus — auch nicht aus einem alten intervalConfig im JSON
            // (D1-Altbestand). Die Weiche haengt am Projekttyp, siehe
            // hatBegehungsrhythmus / planIntervalConfigNachBegehung.
            const mitRhythmus = hatBegehungsrhythmus(meta);
            const updatedIntervalConfig = planIntervalConfigNachBegehung(meta, finalNextDate);

            let billingServiceId = billingData?.serviceId || meta.currentBillingServiceId;

            // ═══════════════════════════════════════════════════════════════
            // AUTO-TRACK: Automatically book 1× inspection service when
            // creating a new report. This ensures every report creates a
            // ServiceLog entry for accurate KPI tracking.
            // ═══════════════════════════════════════════════════════════════
            // Find the inspection service to book
            //
            // AUTO-TRACK: Removed automatic booking here to prevent double entries.
            // Booking now happens exclusively when sending the report in EmailModal.tsx.
            //
            // Deshalb hat dieser Pfad auch KEINE Zweitschrift ins
            // Buchungsjournal: `updatedServiceLog` ist eine unveraenderte
            // Kopie, hier entsteht keine neue Protokollzeile. Die
            // Begehungsbuchung beim Abschliessen haengt am Versandpfad.
            //
            // Pauschalprojekt: der Folgebericht erbt die PAUSCHALE statt einer
            // von ihr GEDECKTEN Begehungsposition (2.7.10) — geerbt waere sie
            // die Vorauswahl des naechsten Versands. Ein Los, das die Pauschale
            // nicht deckt, bleibt stehen (dieselbe Regel wie der Versand).
            billingServiceId = followUpBillingServiceId(updatedServices, billingServiceId);

            const newMeta: ReportMetadata = {
                ...report.metadata,
                inspectionNumber: nextNr,
                date: finalNextDate,
                time: finalNextTime,
                sectionId: sectionIdToUse as string,
                isExported: false,
                currentBillingServiceId: billingServiceId,
                // Aufteilung gehoert zur EINEN Begehung, die sie erzeugt hat — ohne
                // Reset wuerde der neue (nicht aufgeteilte) Folgebericht die Lose der
                // letzten Begehung erben und beim Versand deren Betraege nachbuchen.
                currentBillingSplit: undefined,
                // Anwesenheit gehoert zur EINEN Begehung, die sie erfasst hat.
                // Ohne Reset startet der Folgebericht mit den Teilnehmern der
                // letzten — wer den Haken uebersieht, laesst das Protokoll eine
                // Anwesenheit behaupten, die es nie gab, und unterschreibt sie
                // mit. Den echten Stand haelt der Archiv-Snapshot fest, der
                // weiter oben in dieser Funktion geschrieben wurde.
                distributionList: clearPresence(report.metadata.distributionList || []),
                // Gaeste sind gar nicht erst uebertragbar: sie existieren nur,
                // weil sie an diesem einen Tag dabei waren.
                guests: [],
                intervalConfig: updatedIntervalConfig, // ← includes recalculated nextScheduledDate
                globalData: { ...globalData, services: updatedServices, serviceLog: updatedServiceLog, sections: updatedSections }
            };

            const newReport: Report = {
                ...report,
                metadata: newMeta,
                // Fortschreiben in den Folgebericht:
                // 'good' (Positiv) ist eine Feststellung zu einem Zustand, die
                // mit der Begehung abgeschlossen ist — sie wandert wie bisher
                // in die Historie.
                // 'general' (Information) ist dagegen eine Vorbemerkung an den
                // Leser und war nie eine Aufgabe. Als 'closed' stand sie im
                // Folgebericht unter „Erledigte Mängel", obwohl es nichts zu
                // erledigen gab. Sie wird deshalb unverändert übernommen und
                // bleibt oben im Bericht stehen, wo sie gelöscht oder
                // überschrieben werden kann.
                items: report.items.map(item =>
                    item.status === 'good'
                        ? { ...item, status: 'closed', closedDate: report.metadata.date }
                        : item
                )
            };

            // ── FIX: Update DB with next_scheduled_date + last_inspection_date ──
            // OHNE updated_at (11.09.2026, Vorfall 26073001): updated_at ist der
            // Sync-Anker (projectVersionAnchor). Wer ihn hier verstellt, laesst
            // den gleich folgenden Blob-Save den Anker verfehlen — der geht dann
            // als ueberholter Stand mit counters 'keep-server' raus, und der
            // Zaehler der eben gebuchten Position bleibt beim alten Serverwert.
            // Der Blob-Save schreibt diese Spalten und updated_at sowieso; die
            // Zeile hier ist nur der fruehe Vorgriff. projects hat keinen
            // updated_at-Trigger, der Anker bleibt also unberuehrt.
            if (globalData.projectId) {
                await supabase.from('projects').update({
                    current_inspection_number: nextNr,
                    last_inspection_date: finalNextDate,
                    next_scheduled_date: mitRhythmus ? (updatedIntervalConfig?.nextScheduledDate ?? null) : null,
                }).eq('id', globalData.projectId).select('id, updated_at').maybeSingle();
            }

            // Die Anwesenheiten stehen oben auf null, die Gaeste sind weg. Damit
            // der neue Bericht nicht mit einer leeren Teilnehmerliste weiterlaeuft,
            // merkt sich das Modul, die Auswahl direkt beim Oeffnen anzuzeigen —
            // dort ist "Alleine" die ausdrueckliche Antwort.
            markParticipantPrompt(participantPromptKey(globalData.projectId, nextNr));

            if (onCreateVersion) onCreateVersion(newReport); else onUpdate(newReport);
            onNavigate('module-sigeko-inspection');
        }
        setShowLoadDialog(false);
    };

    /**
     * Handy-Knopf „Begehung #N fortsetzen": der offene Entwurf direkt im Modul —
     * derselbe Schritt wie „Entwurf bearbeiten" im Dialog, nur ohne neue
     * Datums-/Uhrzeitangabe (die des Entwurfs bleiben). Lesemodus und fremdes
     * Projekt gehen wie bisher ueber den Dialog.
     */
    const handleContinueDraft = () => {
        if (fortsetzenWeg(isReadOnly, foreignAccess.pending) === 'dialog') {
            void handleOpenEditorFromGrid();
            return;
        }
        void handleReportAction('edit');
    };

    const handleTrackService = async (
        service: ServiceItem | null,
        amount: number,
        description: string,
        date: string,
        /**
         * FUER WEN gebucht wird (performerChoice). Nur ein Admin waehlt das im
         * Dialog; fehlt es, ist der Ausfuehrende der angemeldete Nutzer.
         */
        performer?: PerformerChoice,
        /**
         * Pflicht-Begruendung einer Buchung auf eine NEP-Position. Eigenes
         * Feld auf der Buchung (ServiceTransaction.nepReason), nie Teil der
         * Beschreibung — auch nicht im Tochter-Wrapper darunter.
         */
        nepReason?: string,
    ) => {
        if (!service) return;
        // Bucht bei verknuepften Projekten direkt ins Mutterprojekt
        // (saveProjectToCloud) und schreibt Begehungsdaten per Supabase-Update —
        // beides umgeht den Lesemodus. Zweite Reihe hinter den bereits
        // gesperrten Einstiegen in ProjectStats.
        if (isReadOnly) {
            // Fremdes Projekt: Begruendung statt Absage — gebucht wird erst
            // mit dem naechsten Klick auf dem frisch geladenen Stand.
            if (foreignAccess.pending) {
                void foreignAccess.requestJustification();
                return;
            }
            alert(readOnlyMessage('Es wurde keine Leistung gebucht.'));
            return;
        }
        const serviceId = service.id;

        // Ausführender der Nachbuchung. Vorher `${first_name} ${last_name}` ohne
        // trim und mit 'System' als Notnagel: Bei fehlendem Nachnamen entstand
        // "Max undefined", bei leerem Profil " " — beides truthy und beides
        // damit stärker als der richtige Name aus dem Berichtsarchiv, denn die
        // Beschreibung einer Nachbuchung enthält oft "Bericht Nr. X" und wird
        // im Leistungsnachweis auf genau diesen Archiveintrag gematcht. Leer
        // heißt jetzt leer, und der Archivname greift.
        // Die Buchhaltung traegt Begehungen FUER einen Koordinator nach — dann
        // muss dessen Name hier stehen, nicht der der Sachbearbeiterin.
        const performerId = performer?.performerId || currentUser?.id || 'system';
        const performerName = performer ? performer.performerName : buildActorName(currentUser);

        // Child project: the stats view lists the PARENT's services, so the
        // tracked quantity must be booked on the parent. Previously the lookup
        // below silently found nothing (parent service id does not exist in the
        // child's services) and the entry was lost without any feedback.
        const linkConfig = globalData.linkedProjectConfig;
        if (linkConfig?.role === 'child' && linkConfig.linkedProjectId) {
            // Die Zweitschrift wird erst NACH dem try angehaengt: sie darf nie
            // in einem Block stehen, dessen catch dem Anwender einen Fehler zu
            // einer Buchung zeigt, die laengst geschrieben ist.
            let spiegelung: JournalMirrorRequest | null = null;
            try {
                const freshParent = await loadProjectById(linkConfig.linkedProjectId);
                if (!freshParent) throw new Error('Mutterprojekt nicht gefunden');
                const parentMeta = freshParent.data.metadata as ReportMetadata;
                // Dieselbe Weiche wie im eigenen Projekt: ist das Ziel eine
                // Pauschale der MUTTER, wird es auch von hier aus nur eine
                // Nachweiszeile — ihr Monat zaehlt allein ueber FLAT-JJJJ-MM.
                const plan = planTrackServiceBooking({
                    services: parentMeta.globalData?.services || [],
                    serviceId,
                    amount,
                    entry: { id: crypto.randomUUID(), date, bookedAt: new Date().toISOString(), performerId, performerName, description: `Tochter-Projekt "${meta.projectName}": ${description}`, nepReason },
                });
                if (!plan) {
                    // Paketposition der Mutter: gebucht wird ueber ihre Bestandteile (Kernregel 7).
                    const paket = hatBestandteile((parentMeta.globalData?.services || []).find(s => s.id === serviceId));
                    alert(paket
                        ? 'Diese Position hat Bestandteile und wird über die Bestandteile gebucht — im Mutterprojekt.'
                        : 'Leistung im Mutterprojekt nicht gefunden.');
                    return;
                }
                const parentServices = plan.services;
                const parentLogEntry = plan.logEntry;
                const updatedParent = { ...freshParent.data, metadata: { ...parentMeta, globalData: { ...parentMeta.globalData, services: parentServices, serviceLog: [parentLogEntry, ...(parentMeta.globalData?.serviceLog || [])] } } };
                await saveProjectToCloud(updatedParent, linkConfig.linkedProjectId);
                // Gebucht wird auf die MUTTER, also gehoert die Zeile in DEREN
                // Journal: Projekt-id, Protokollstand und Verknuepfung sind die
                // des Mutterprojekts. Die Abrechnungsart liest die Migration
                // ebenfalls am Projekt der ZEILE — mit der Verknuepfung der
                // Tochter liefen Client und Nr. 60 auseinander.
                //
                // `statements` nur bei der Nachweiszeile: dort sagt die Regel
                // der Pauschale (isInspectionProofEntry), ob sie eine Begehung
                // belegt — die Abbildung saehe nur Menge 0 und verneinte es.
                // Sonst keine: die Beschreibung kommt aus dem Buchungsdialog
                // und ist freier Text; erschliessen darf es die Abbildung,
                // raten darf es niemand.
                spiegelung = {
                    projectId: linkConfig.linkedProjectId,
                    entries: [parentLogEntry],
                    previousLog: getServiceLog({ metadata: parentMeta }),
                    source: 'child_to_parent',
                    linkedProjectConfig: parentMeta.globalData?.linkedProjectConfig ?? null,
                    ...(plan.statements ? { statements: plan.statements } : {}),
                };
                setLinkedParent(updatedParent);
            } catch (e) {
                console.error('Tracking on parent project failed', e);
                alert('Fehler: Leistung konnte nicht im Mutterprojekt erfasst werden.');
            }
            if (spiegelung) await mirrorEntriesToJournal(spiegelung);
            return;
        }

        const plan = planTrackServiceBooking({
            services: globalData.services || [],
            serviceId,
            amount,
            entry: { id: crypto.randomUUID(), date, bookedAt: new Date().toISOString(), performerId, performerName, description, nepReason },
        });
        if (plan) {
            const { services, logEntry } = plan;

            // Eine Buchung auf einer Begehungsleistung schreibt den Rhythmus des
            // Projekts fort — ABER nur, wenn sie eine stattgefundene Begehung
            // meldet. Ein rueckdatierter Nachtrag fuer die Abrechnung buchte
            // frueher denselben Weg und drehte Berichtsdatum, letzte und
            // naechste Begehung mit sich zurueck (Vorfall 6146: Nachtrag mit
            // Leistungsdatum 05.01.2026 setzte den Folgetermin vom 25.08. auf
            // den 13.01. zurueck). planInspectionScheduleUpdate entscheidet das
            // anhand des bisherigen Berichtsdatums und gibt sonst null.
            //
            // Im Pauschalprojekt ist die Begehung eine Nachweiszeile auf der
            // Pauschale — sie schreibt den Rhythmus genauso fort, sobald sie
            // als Begehung zaehlt (plan.isInspection, isInspectionProofEntry).
            const isInspection = plan.isInspection;
            let updatedIntervalConfig = meta.intervalConfig;
            let updatedReportDate = meta.date;

            // SiFa: kein Rhythmus, also auch kein Folgetermin und kein
            // weekly-Vorschlag — unabhaengig davon, ob ein altes intervalConfig
            // im JSON liegt (siehe hatBegehungsrhythmus).
            if (isInspection && hatBegehungsrhythmus(meta) && globalData.projectId && globalData.projectId !== 'temp-id') {
                const currentIntervalConfig = meta.intervalConfig || { type: 'weekly' as IntervalType };
                const schedule = planInspectionScheduleUpdate(date, meta.date, currentIntervalConfig);

                if (schedule) {
                    updatedIntervalConfig = { ...currentIntervalConfig, nextScheduledDate: schedule.nextScheduledDate };
                    updatedReportDate = schedule.date;

                    // Update DB directly — OHNE updated_at. Das ist der Sync-Anker
                    // (projectVersionAnchor): mit einem neuen Wert hier verfehlte
                    // der gleich folgende Blob-Save aus onUpdate den Anker, lief
                    // als ueberholter Stand mit counters 'keep-server' und liess
                    // quantity_used beim alten Serverwert. Projekt 26073001
                    // (11.09.2026): 21 August-Buchungen im Protokoll, keine im
                    // Zaehler — genau die, die den Rhythmus fortgeschrieben
                    // haben. Rueckdatierte (schedule null) kamen hier nie vorbei
                    // und wurden gezaehlt.
                    await supabase.from('projects').update({
                        last_inspection_date: schedule.date,
                        next_scheduled_date: schedule.nextScheduledDate,
                    }).eq('id', globalData.projectId).select('id, updated_at').maybeSingle();
                }
            }

            onUpdate({
                ...report,
                metadata: {
                    ...report.metadata,
                    date: updatedReportDate,
                    intervalConfig: updatedIntervalConfig,
                    globalData: { ...globalData, services, serviceLog: [...(globalData.serviceLog || []), logEntry] }
                }
            });
            // Muster B: geschrieben wird erst ueber die Offline-Queue, deshalb
            // nur vormerken — angehaengt wird zentral nach dem bestaetigten
            // Flush. Quelle 'manual_billing': von Hand angestossene Verbuchung
            // einer Menge aus dem Buchungsdialog, kein Beleg-Rueckschreiben und
            // keine Korrektur aus der Buchhaltungsansicht. `statements` nur bei
            // der Nachweiszeile (siehe Kindpfad oben) — sonst ist die
            // Beschreibung freier Text.
            if (globalData.projectId && globalData.projectId !== 'temp-id') {
                queueJournalMirror({
                    projectId: globalData.projectId,
                    entries: [logEntry],
                    previousLog: getServiceLog(report),
                    source: 'manual_billing',
                    linkedProjectConfig: globalData.linkedProjectConfig ?? null,
                    ...(plan.statements ? { statements: plan.statements } : {}),
                });
            }
        } else if (hatBestandteile(service)) {
            // Zweite Reihe: der Buchungsdialog bietet Pakete gar nicht an
            // (ServiceTrackingModal leitet in die Bestandteile um).
            console.warn('Paketposition wird nur über ihre Bestandteile gebucht — keine Mengenbuchung.', service.id);
        }
    };

    /**
     * MONATSPAUSCHALE: einen offenen Monat aus dem Vorschlag buchen
     * (Protokolleintrag 'FLAT-YYYY-MM', description 'Monatspauschale MM/YYYY').
     *
     * Die Regeln — Schluessel-Riegel gegen Doppelbuchung, Menge frei aber > 0,
     * nur offene Laufzeitmonate — liegen in flatRateBooking und laufen hier
     * ERNEUT gegen den aktuellen Stand. Geschrieben wird ueber den
     * onUpdate-Pfad wie bei handleTrackService (App.handleUpdateReport haelt
     * den Sync-Anker); nur das Tochterprojekt bucht wie dort direkt auf das
     * frisch geladene Mutterprojekt, denn die Pauschalposition lebt im
     * Mutter-LV. Ergebnis als { ok, message } fuer den Buchungsdialog —
     * gebucht wird NUR auf dessen Klick, nie beim Oeffnen einer Ansicht.
     */
    const handleBookFlatMonth = async (
        flatServiceId: string,
        monthKey: string,
        quantity: number,
    ): Promise<{ ok: boolean; message: string }> => {
        if (isReadOnly) {
            // Der Buchungsdialog erwartet eine Antwort; er bekommt den Grund,
            // waehrend daneben der Begruendungsdialog aufgeht.
            if (foreignAccess.pending) {
                void foreignAccess.requestJustification();
                return { ok: false, message: FOREIGN_ERST_BEGRUENDEN };
            }
            const message = readOnlyMessage('Es wurde kein Pauschalmonat gebucht.');
            alert(message);
            return { ok: false, message };
        }
        const now = new Date().toISOString();
        const provision = {
            entryId: crypto.randomUUID(),
            now,
            actorName: buildActorName(currentUser),
            actorId: currentUser?.id,
        };
        const draft = { flatServiceId, monthKey, quantity };

        // Tochterprojekt: die Anzeige listet die Leistungen der MUTTER, die
        // Buchung gehoert dorthin — frisch geladen, damit der Schluessel-Riegel
        // gegen den echten Stand prueft (dieselbe Struktur wie in
        // handleTrackService).
        const linkConfig = globalData.linkedProjectConfig;
        if (linkConfig?.role === 'child' && linkConfig.linkedProjectId) {
            try {
                const freshParent = await loadProjectById(linkConfig.linkedProjectId);
                if (!freshParent) throw new Error('Mutterprojekt nicht gefunden');
                const parentMeta = freshParent.data.metadata as ReportMetadata;
                const parentGlobal = parentMeta.globalData;
                const result = planFlatMonthBooking(
                    {
                        services: parentGlobal?.services || [],
                        serviceLog: parentGlobal?.serviceLog || [],
                        startDate: parentGlobal?.startDate,
                        endDate: parentGlobal?.endDate,
                        // Offene Pauschalmonate nach dem Berliner Tag (nicht UTC): am
                        // Monatsersten ist der neue Monat ab 00:00 buchbar, nicht erst ab 02:00.
                        today: heuteBerlin(),
                    },
                    draft,
                    provision,
                );
                if (!result.ok || !result.plan) {
                    return { ok: false, message: result.error || 'Die Monatsbuchung konnte nicht geplant werden.' };
                }
                const updatedParent = {
                    ...freshParent.data,
                    metadata: {
                        ...parentMeta,
                        globalData: {
                            ...parentGlobal,
                            services: result.plan.services,
                            serviceLog: [result.plan.logEntry, ...(parentGlobal?.serviceLog || [])],
                        },
                    },
                };
                await saveProjectToCloud(updatedParent, linkConfig.linkedProjectId);
                // Zweitschrift auf die MUTTER (Projekt-id, Protokollstand und
                // Verknuepfung sind deren eigene, wie oben in
                // handleTrackService). Quelle bleibt 'flat_rate' und wird nicht
                // 'child_to_parent': die Zeile ist eine Monatspauschale, und
                // genau daran erkennt sie spaeter jemand wieder — dass sie von
                // der Tochter angestossen wurde, steht in der Beschreibung.
                //
                // Ohne `await`: eine abgelehnte Zusage duerfte den catch dieses
                // Blocks nie erreichen, der dem Anwender eine gescheiterte
                // Buchung meldet. `mirrorEntriesToJournal` wirft laut Modul nie.
                //
                // Keine `statements`: eine Pauschale ist weder Zusatzleistung
                // noch Begehungsnachweis — beides erschliesst die Abbildung
                // schon aus dem 'FLAT-'-Schluessel.
                void mirrorEntriesToJournal({
                    projectId: linkConfig.linkedProjectId,
                    entries: [result.plan.logEntry],
                    previousLog: parentGlobal?.serviceLog || [],
                    source: 'flat_rate',
                    linkedProjectConfig: parentGlobal?.linkedProjectConfig ?? null,
                });
                setLinkedParent(updatedParent);
                return { ok: true, message: `${result.plan.label} im Mutterprojekt gebucht (Menge ${result.plan.logEntry.quantity.toLocaleString('de-DE')}).` };
            } catch (e: any) {
                console.error('Flat month booking on parent project failed', e);
                return { ok: false, message: `Die Buchung im Mutterprojekt ist fehlgeschlagen: ${e?.message || e}` };
            }
        }

        const result = planFlatMonthBooking(
            {
                services: globalData.services || [],
                serviceLog: getServiceLog(report),
                startDate: globalData.startDate,
                endDate: globalData.endDate,
                // Berliner Tag wie oben (offene Pauschalmonate).
                today: heuteBerlin(),
            },
            draft,
            provision,
        );
        if (!result.ok || !result.plan) {
            return { ok: false, message: result.error || 'Die Monatsbuchung konnte nicht geplant werden.' };
        }
        onUpdate({
            ...report,
            metadata: {
                ...report.metadata,
                globalData: {
                    ...globalData,
                    services: result.plan.services,
                    serviceLog: [...(globalData.serviceLog || []), result.plan.logEntry],
                },
            },
        });
        // Muster B wie bei handleTrackService: erst nach dem bestaetigten Flush
        // anhaengen. Quelle 'flat_rate', keine `statements` (siehe oben).
        if (globalData.projectId && globalData.projectId !== 'temp-id') {
            queueJournalMirror({
                projectId: globalData.projectId,
                entries: [result.plan.logEntry],
                previousLog: getServiceLog(report),
                source: 'flat_rate',
                linkedProjectConfig: globalData.linkedProjectConfig ?? null,
            });
        }
        return { ok: true, message: `${result.plan.label} gebucht (Menge ${result.plan.logEntry.quantity.toLocaleString('de-DE')}).` };
    };

    const handleConfirmCorrection = async (correctedNumber: number | null, correctedDate: string | null) => {
        if (!globalData.projectId || globalData.projectId === 'temp-id') return;
        // Ueberschreibt die komplette data-Spalte des Projekts. Das
        // DataCorrectionModal kennt keinen Abbrechen-Knopf, deshalb bleibt es im
        // Lesemodus schon in ProjectHub zu; dieser Riegel haelt den Pfad auch
        // dann, wenn das Modal woanders eingehaengt wird.
        if (isReadOnly) {
            if (foreignAccess.pending) {
                void foreignAccess.requestJustification();
                return;
            }
            alert(readOnlyMessage('Die Berichtsnummer wurde nicht korrigiert.'));
            return;
        }
        const updatedMeta = { ...meta };
        if (correctedNumber !== null) updatedMeta.inspectionNumber = correctedNumber;
        if (correctedDate !== null) updatedMeta.date = correctedDate || meta.date;
        const updatedReport = { ...report, metadata: updatedMeta };
        try {
            const { error } = await supabase.from('projects').update({ is_legacy_data_corrected: true, current_inspection_number: correctedNumber !== null ? correctedNumber : meta.inspectionNumber, last_inspection_date: correctedDate !== null ? correctedDate : meta.date, data: updatedReport }).eq('id', globalData.projectId);
            if (error) throw error;
            onUpdate(updatedReport);
            setIsLegacyCorrected(true);
            setDbCurrentNr(correctedNumber !== null ? correctedNumber : meta.inspectionNumber);
        } catch (e) { console.error("Correction failed", e); alert("Fehler bei der Daten-Korrektur."); }
    };

    return {
        nextDate, setNextDate,
        nextTime, setNextTime,
        showLoadDialog, setShowLoadDialog,
        trackingModalState, setTrackingModalState,
        /** Fremdes Projekt ohne Begruendung von heute: der Begehungsknopf sagt es. */
        begruendungMoeglich: foreignAccess.pending,
        handleOpenEditorFromGrid,
        handleContinueDraft,
        handleReportAction,
        handleTrackService,
        handleBookFlatMonth,
        handleConfirmCorrection,
    };
};
